import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import api from "@/lib/api";
import {
  Shield, Download, Trash2, ToggleLeft, ToggleRight, FileText,
  Loader2, AlertTriangle, Check, X, ArrowLeft, Bird, Lock, Eye
} from "lucide-react";

export default function PrivacySettings({ onBack }) {
  const { auth, setAuth } = useAppContext();
  const nav = useNavigate();
  const [consents, setConsents] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [exportData, setExportData] = useState(null);

  useEffect(() => {
    if (!auth?.jwt) return;
    (async () => {
      try {
        const c = await api.getConsents(auth.jwt);
        const map = {};
        c.forEach(r => { map[r.consent_type] = r.granted; });
        setConsents(map);
      } catch (e) {}
      setLoading(false);
    })();
  }, [auth?.jwt]);

  const toggleConsent = useCallback(async (type) => {
    const newVal = !consents[type];
    setConsents(prev => ({ ...prev, [type]: newVal }));
    try {
      await api.recordConsent(auth.jwt, type, newVal);
    } catch (e) {}
  }, [auth?.jwt, consents]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const data = await api.exportMyData(auth.jwt);
      setExportData(data);
      // Also trigger download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pigeon-supply-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      void 0;
    }
    setExporting(false);
  }, [auth?.jwt]);

  const handleDelete = useCallback(async () => {
    if (!confirmEmail) { setDeleteError("Enter your email to confirm"); return; }
    setDeleting(true);
    setDeleteError("");
    try {
      await api.deleteAccount(auth.jwt, confirmEmail);
      localStorage.removeItem("pigeon-auth");
      setAuth(null);
      nav("/landing");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setDeleteError(typeof detail === "string" ? detail : "Deletion failed");
    }
    setDeleting(false);
  }, [auth?.jwt, confirmEmail, nav, setAuth]);

  const CONSENT_ITEMS = [
    { key: "essential", label: "Essential Data Processing", desc: "Required for platform to function. Cannot be disabled.", forced: true },
    { key: "voice", label: "Voice Processing", desc: "Speech-to-text and text-to-speech processed locally in your browser. No voice data is stored on our servers." },
    { key: "ai_processing", label: "AI Hub Assistant", desc: "Hub context (site names, machine types) shared with AI for assistant responses. No personal data sent." },
    { key: "activity_logging", label: "Activity Logging", desc: "Records shift times, login timestamps for HR reporting. Visible to hub planner." },
  ];

  return (
    <div className="min-h-[100dvh] bg-white" data-testid="privacy-settings">
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-xl bg-slate-100 text-slate-400 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Shield className="w-6 h-6 text-lime-500" />
          <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">Privacy & Data</h1>
        </div>
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">GDPR Art. 15-20</span>
      </header>

      <main className="max-w-2xl mx-auto p-6 space-y-6">
        {/* User info */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex items-center gap-4">
          <div className="w-12 h-12 bg-lime-500 rounded-xl flex items-center justify-center">
            <Bird className="w-6 h-6 text-slate-950" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900">{auth?.user?.email}</p>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
              {auth?.user?.subscription_tier} tier / {auth?.user?.has_password ? "Password set" : "Magic link only"}
            </p>
          </div>
        </div>

        {/* Consent Management */}
        <section>
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
            <ToggleRight className="w-4 h-4 text-lime-500" /> Data Processing Consents
          </h2>
          <div className="space-y-2">
            {CONSENT_ITEMS.map(item => (
              <div key={item.key} className="p-4 bg-white border border-slate-200 rounded-xl flex items-center justify-between" data-testid={`consent-${item.key}`}>
                <div className="flex-1 mr-4">
                  <p className="text-xs font-bold text-slate-900">{item.label}</p>
                  <p className="text-[10px] text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
                {item.forced ? (
                  <span className="px-3 py-1.5 bg-lime-100 text-lime-700 rounded-lg text-[9px] font-black uppercase">Required</span>
                ) : (
                  <button
                    onClick={() => toggleConsent(item.key)}
                    className={`w-12 h-7 rounded-full transition-colors flex items-center ${consents[item.key] ? "bg-lime-500 justify-end" : "bg-slate-200 justify-start"}`}
                  >
                    <div className="w-5 h-5 bg-white rounded-full shadow-sm mx-1" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Data Export */}
        <section>
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Download className="w-4 h-4 text-blue-500" /> Export My Data
          </h2>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-xs text-blue-700 mb-3">Download all personal data stored by Pigeon Supply as a JSON file. This includes your account info, hub memberships, activity logs, and messages.</p>
            <button
              data-testid="export-data-btn"
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {exporting ? "Exporting..." : "Download My Data"}
            </button>
            {exportData && (
              <p className="text-[10px] text-blue-500 mt-2 flex items-center gap-1">
                <Check className="w-3 h-3" /> Export downloaded — {new Date().toLocaleDateString()}
              </p>
            )}
          </div>
        </section>

        {/* Data Retention Info */}
        <section>
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Eye className="w-4 h-4 text-slate-500" /> Data Retention
          </h2>
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            {[
              ["Account data", "Stored until you delete your account"],
              ["Activity logs", "365 days, then automatically purged"],
              ["Auth tokens", "Purged after expiry (72h for JWT, 1h for reset)"],
              ["Voice data", "Never stored — processed entirely in your browser"],
              ["Payment data", "Processed by Stripe — we only store transaction IDs"],
            ].map(([label, val], i) => (
              <div key={i} className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-500 uppercase">{label}</span>
                <span className="text-[10px] text-slate-400">{val}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Delete Account */}
        <section>
          <h2 className="text-sm font-black text-red-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Delete Account
          </h2>
          {!showDelete ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-xs text-red-600 mb-3">Permanently delete your account and anonymize all personal data in hub logs. This action cannot be undone.</p>
              <button
                data-testid="show-delete-btn"
                onClick={() => setShowDelete(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all"
              >
                <Trash2 className="w-4 h-4" /> Delete My Account
              </button>
            </div>
          ) : (
            <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl space-y-3" data-testid="delete-confirm-panel">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <p className="text-xs font-black text-red-700 uppercase">Confirm Account Deletion</p>
              </div>
              <p className="text-xs text-red-600">Type your email to confirm. All personal data will be erased. Hub operational data will be anonymized.</p>
              <input
                data-testid="delete-confirm-email"
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={auth?.user?.email}
                className="w-full px-4 py-3 bg-white border border-red-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:border-red-500 outline-none"
              />
              {deleteError && <p className="text-xs text-red-500 font-medium">{deleteError}</p>}
              <div className="flex gap-2">
                <button onClick={() => { setShowDelete(false); setConfirmEmail(""); setDeleteError(""); }}
                  className="flex-1 py-3 bg-slate-200 text-slate-600 rounded-xl text-xs font-black uppercase">Cancel</button>
                <button
                  data-testid="confirm-delete-btn"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl text-xs font-black uppercase flex items-center justify-center gap-2 transition-all"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> Permanently Delete</>}
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
